import React from 'react'
import ReactDOM from 'react-dom/client'
import { initAnalytics } from '../lib/analytics'
import DistributionWidget from './DistributionWidget'
import styles from './styles.css?inline'

// Standalone Distribution SDK. Same partner-facing pattern as OddsWidget
// (script tag + init()) but a separate, much smaller bundle so partners
// who only embed the carrier grid don't pay for Tailwind, react-query,
// the bet slip overlay, etc.
//
// Usage on partner site:
//
//   <script src="https://grid.altsportsdata.com/distribution-widget.umd.js"></script>
//   <div id="ad-distribution"></div>
//   <script>
//     DistributionWidget.init('#ad-distribution', {
//       sport: 'f1',
//       apiBase: 'https://grid.altsportsdata.com',
//       maxBooks: 5   // optional — cap the grid at the top N books
//     })
//   </script>
//
// Sportsbook mode — the sports a book carries, as sport-logo tiles linking
// to that book's per-sport affiliate URL:
//
//     DistributionWidget.init('#ad-distribution', {
//       sportsbook: 'draftkings',
//       apiBase: 'https://grid.altsportsdata.com'
//     })

export interface DistributionWidgetConfig {
  /** Sport slug — renders the books carrying it. One of sport / sportsbook is required. */
  sport?: string
  /** Sportsbook slug (slugified name, e.g. 'draftkings', 'hard-rock-bet') — renders the sports it carries. */
  sportsbook?: string
  apiBase?: string
  // Cap the grid at the first N books. The API already orders carriers by
  // priority (DraftKings, Polymarket, FanDuel, Underdog, PrizePicks, then
  // alphabetical), so N=5 means "the top 5 books". Omit for all books.
  maxBooks?: number
  // Sub-affiliate embed flag. Tiles link to the sub-affiliate's own
  // book-issued URLs instead of ours; books they have no link for render
  // unlinked. Server-side swap via ?aff= — an unknown flag unlinks all.
  subAffiliate?: string
}

export interface DistributionWidgetInstance {
  destroy: () => void
}

const DEFAULT_API_BASE = 'https://grid.altsportsdata.com'

class DistributionWidgetSDK {
  private instances = new Map<HTMLElement, { root: ReactDOM.Root }>()

  init(container: string | HTMLElement, config: DistributionWidgetConfig): DistributionWidgetInstance {
    const el = typeof container === 'string'
      ? document.querySelector(container) as HTMLElement | null
      : container

    if (!el) {
      throw new Error(`DistributionWidget.init: container not found (${String(container)})`)
    }

    initAnalytics()

    // Re-init on the same container destroys the previous instance so
    // partners can swap sports without leaking React roots.
    const existing = this.instances.get(el)
    if (existing) {
      existing.root.unmount()
      this.instances.delete(el)
    }

    const shadow = el.shadowRoot ?? el.attachShadow({ mode: 'open' })

    if (!shadow.querySelector('style')) {
      const styleEl = document.createElement('style')
      styleEl.textContent = styles
      shadow.appendChild(styleEl)
    }

    const mountPoint = document.createElement('div')
    shadow.appendChild(mountPoint)

    const root = ReactDOM.createRoot(mountPoint)
    root.render(React.createElement(DistributionWidget, {
      sport: config.sport,
      sportsbook: config.sportsbook,
      apiBase: config.apiBase ?? DEFAULT_API_BASE,
      maxBooks: config.maxBooks,
      subAffiliate: config.subAffiliate,
    }))

    const instance: DistributionWidgetInstance = {
      destroy: () => {
        root.unmount()
        this.instances.delete(el)
      },
    }
    this.instances.set(el, { root })
    return instance
  }
}

const sdk = new DistributionWidgetSDK()

declare global {
  interface Window {
    DistributionWidget: typeof sdk
  }
}

if (typeof window !== 'undefined') {
  window.DistributionWidget = sdk
}

export default sdk
